import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { StringFormattingService } from '../../../services';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';

@Injectable({ providedIn: 'root' })
export class RemoveTagsFromOrderService extends AutomationDisplayService {
  constructor(
    protected stringFormattingService: StringFormattingService,
    protected translateService: TranslateService,
  ) {
    super();
  }

  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    // The error case is simple: the result is static text.
    if (activity?.error) {
      return this.tableEntry(
        this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTIVITY.FAILED_TO_REMOVE_TAGS'),
        this.emptyBody(),
      );
    }

    // Otherwise, we need to carefully select the body text.
    // Show user-friendly text if we know the tags, and the remove-all text if they're removing everything.
    // Fallback to an empty string if something is wrong with the data.
    const data = activity?.workflowStep?.data ? JSON.parse(activity.workflowStep.data) : {};
    let body$: Observable<string> = of('');
    if (data.remove_all_tags) {
      body$ = this.translateService.stream(
        'AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTIVITY.SUBTITLE_REMOVE_ALL',
      );
    } else if (data.tags && data.tags.length > 0) {
      // Tags at this point should already be sanitized, so we simply have to format them.
      const subtitle =
        data.tags.length === 1
          ? 'AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTIVITY.SUBTITLE_SINGULAR'
          : 'AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTIVITY.SUBTITLE_PLURAL';
      body$ = this.translateService.stream(subtitle, { tags: this.stringFormattingService.formatList(data.tags) });
    }
    return this.tableEntry(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTIVITY.TITLE'),
      body$,
    );
  }

  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    // Handle the known cases first.
    const data = step?.data ? JSON.parse(step.data) : {};
    let body$: Observable<string>;
    if (data.remove_all_tags) {
      body$ = this.translateService.stream(
        'AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTION.SUBTITLE_REMOVE_ALL',
      );
    } else if (data.tags && data.tags.length > 0) {
      // Tags at this point should already be sanitized, so we simply have to format them.
      const subtitle =
        data.tags.length === 1
          ? 'AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTION.SUBTITLE_SINGULAR'
          : 'AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTION.SUBTITLE_PLURAL';
      body$ = this.translateService.stream(subtitle, { tags: this.stringFormattingService.formatList(data.tags) });
    }
    if (body$) {
      return this.nodeDisplayData(body$, this.noValidationErrorsDetected());
    }

    // If something is wrong with the data, show validation error information
    return this.nodeDisplayData(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTION.TITLE'),
      this.nonDismissibleValidationErrorsDetected(
        step,
        this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.REMOVE_TAGS_FROM_ORDER.ACTION.CONFIG_ERROR'),
      ),
    );
  }
}
